import { useCallback, useRef, useState } from 'react';
import { TabPanel, Tabs } from 'react-tabs';

import { StringML } from '@/lib/entities/domain';

import {
  RenderTabList,
  useTabListState,
} from '@/lib/shared/components/Gallery/GalleryModal';
import { H1, H2, P2 } from '@/lib/shared/components/Typography';
import { useSwipeDirection } from '@/lib/shared/hooks/use-swipe-direction';
import { useWindowSize } from '@/lib/shared/hooks/use-window-size';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import styles from './HoverableTabsBlock.module.scss';

interface IHoverableTabs {
  slides: {
    image: string;
    mobileImage: string;
    tabletImage?: string;
    tabletBigImage?: string;
    tabTitle: string;
    text: string;
    excludeMobileGradient?: boolean;
  }[];
  gridTemplateColumn: StringML;
  /**
   * Для перевода
   */
  title: string;
  secondTitle?: string;
  theme?: 'light' | 'blue';
  removeBgImage?: boolean;
  largeTitle?: boolean;
  smallPadding?: boolean;
}
export const HoverableTabsBlock = ({
  slides,
  title,
  gridTemplateColumn,
  removeBgImage,
  theme = 'light',
  largeTitle,
  smallPadding,
  secondTitle,
}: IHoverableTabs) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('index');
  const { width } = useWindowSize();
  const [selected, setSelected] = useState(0);
  const isTabAnimated = useRef(false);
  const tabHeaders = slides.map((slide) => t(slide.tabTitle));
  const tabListState = useTabListState({
    tabHeaders,
    isTabAnimated,
    windowWidth: width,
    selected,
  });
  const tabRef = useRef<HTMLDivElement>(null);

  const onSelect = useCallback((index: number) => {
    setSelected(index);
  }, []);

  const increaseSlide = useCallback(() => {
    if (!tabRef.current) return;
    const index = selected === 0 ? slides.length - 1 : selected - 1;
    const tab = tabRef.current.querySelectorAll('[role="tab"]')[
      index
    ] as HTMLLIElement;
    const tablist = tabRef.current.querySelector(
      '[role="tablist"]'
    ) as HTMLUListElement;
    const offset = tab.offsetLeft;
    tablist.scrollLeft = offset;
    setSelected(index);
  }, [selected, slides.length]);

  const decreaseSlide = useCallback(() => {
    if (!tabRef.current) return;
    const index = selected === slides.length - 1 ? 0 : selected + 1;
    const tab = tabRef.current.querySelectorAll('[role="tab"]')[
      index
    ] as HTMLLIElement;
    const tablist = tabRef.current.querySelector(
      '[role="tablist"]'
    ) as HTMLUListElement;
    const offset = tab.offsetLeft;
    tablist.scrollLeft = offset;
    setSelected(index);
  }, [selected, slides.length]);

  const swipeEvents = useSwipeDirection({
    onSwipeLeft: decreaseSlide,
    onSwipeRight: increaseSlide,
    minSwipeDistance: 110,
  });

  const throttleSelect = (index: number) => {
    if (!tabRef.current) return;
    (
      tabRef.current.querySelectorAll('[role="tab"]')[index] as HTMLLIElement
    ).click();
  };

  const handleMouseOver = (index: number) => {
    throttleSelect(index);
  };

  return (
    <div
      className={clsx(
        styles.hoverableTabs,
        theme === 'blue' && styles['hoverableTabs--blue'],
        theme === 'light' && styles['hoverableTabs--light'],
        smallPadding && styles['hoverableTabs--small-padding']
      )}
    >
      <div className={styles.container} ref={tabRef}>
        {!removeBgImage && (
          <Image
            sizes="1474px"
            src={'/leaves/palms-transparent-landing.png'}
            alt=""
            width={1474}
            height={1011}
            className={styles.hoverableTabs__bg}
          />
        )}
        {largeTitle ? (
          <H1
            as={'h2'}
            className={clsx(
              styles.hoverableTabs__title,
              largeTitle && styles['hoverableTabs__title--large']
            )}
          >
            {t(title)}
            <br />
            {secondTitle && t(secondTitle)}
          </H1>
        ) : (
          <H2
            className={clsx(
              styles.hoverableTabs__title,
              largeTitle && styles['hoverableTabs__title--large']
            )}
          >
            {t(title)}
            <br />
            {secondTitle && t(secondTitle)}
          </H2>
        )}
        <Tabs
          onSelect={onSelect}
          focusTabOnClick={false}
          forceRenderTabPanel
          selectedIndex={selected}
          className={styles.hoverableTabs__tabs}
        >
          {RenderTabList({
            tabHeaders,
            isTabAnimated,
            theme: theme === 'blue' ? 'bottom-border-light' : 'bottom-border',
            ...tabListState,
          })}
          <div className={styles.hoverableTabs__wrapper}>
            <ul
              className={styles.hoverableTabs__hoverable}
              style={{
                gridTemplateColumns:
                  gridTemplateColumn[language as 'ru' | 'en'],
              }}
            >
              <li onMouseOver={() => handleMouseOver(0)}></li>
              <li onMouseOver={() => handleMouseOver(1)}></li>
              <li onMouseOver={() => handleMouseOver(2)}></li>
              <li onMouseOver={() => handleMouseOver(3)}></li>
              <li onMouseOver={() => handleMouseOver(4)}></li>
            </ul>
            {slides.map((slide, i) => {
              return (
                <TabPanel key={slide.tabTitle}>
                  <div
                    className={clsx(
                      styles.hoverableTabs__slide,
                      selected === i && styles['hoverableTabs__slide--active']
                    )}
                    {...swipeEvents}
                  >
                    <div
                      className={clsx(
                        styles.hoverableTabs__gradient,
                        slide.excludeMobileGradient &&
                          styles['hoverableTabs__gradient--none']
                      )}
                    ></div>
                    <P2 className={styles.hoverableTabs__slideText}>
                      {t(slide.text)}
                    </P2>
                    <Image
                      className={clsx(
                        styles.hoverableTabs__slideImage,
                        styles['hoverableTabs__slideImage--desktop']
                      )}
                      src={slide.image}
                      width={1344}
                      height={335}
                      loader={({ src }) => src + ' '}
                      alt={slide.tabTitle}
                    />
                    <Image
                      className={clsx(
                        styles.hoverableTabs__slideImage,
                        styles['hoverableTabs__slideImage--mobile']
                      )}
                      src={slide.mobileImage}
                      width={735}
                      height={445}
                      loader={({ src }) => src + ' '}
                      alt={slide.tabTitle}
                    />
                    <Image
                      className={clsx(
                        styles.hoverableTabs__slideImage,
                        styles['hoverableTabs__slideImage--tablet']
                      )}
                      src={slide.tabletImage || slide.mobileImage}
                      width={996}
                      height={445}
                      loader={({ src }) => src + ' '}
                      alt={slide.tabTitle}
                    />
                    <Image
                      className={clsx(
                        styles.hoverableTabs__slideImage,
                        styles['hoverableTabs__slideImage--tabletBig']
                      )}
                      src={slide.tabletBigImage || slide.image}
                      width={1200}
                      height={335}
                      loader={({ src }) => src + ' '}
                      alt={slide.tabTitle}
                    />
                  </div>
                </TabPanel>
              );
            })}
          </div>
        </Tabs>
      </div>
    </div>
  );
};
